"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { MessageSquarePlus, X, Loader2, Check, Send } from "lucide-react";
import { trackGaEvent } from "@/lib/gtag";
import { cn } from "@/lib/utils";
import { useI18n } from "@/components/providers/I18nProvider";
import type { MessageKey } from "@/lib/i18n/messages";

/**
 * Floating feedback widget, available across the app.
 * Bottom-left floating feedback control with a delayed nudge to encourage
 * participation if users spend time in the app.
 */

const CATEGORIES = [
  { id: "idea", labelKey: "feedback.idea" },
  { id: "bug", labelKey: "feedback.bug" },
  { id: "praise", labelKey: "feedback.loveIt" },
  { id: "other", labelKey: "feedback.other" },
] satisfies { id: string; labelKey: MessageKey }[];

const FEEDBACK_NUDGE_DELAY_MS = 60_000;
const FEEDBACK_NUDGE_NEVER_SHOW_KEY = "nc_feedback_nudge_never_show";
const FEEDBACK_NUDGE_SESSION_KEY = "nc_feedback_nudge_session_shown";

export function FeedbackWidget() {
  const { t } = useI18n();
  const pathname = usePathname();
  // Private bearer links must not be copied into feedback metadata or nudges.
  const isOnboarding = pathname === "/onboarding" || pathname?.startsWith("/onboarding/") || /^\/(?:editor-review|author-review|manuscript-request|review-invitation)\//.test(pathname ?? "");
  const isEditorProject = /^\/editors\/[^/]+\/?$/.test(pathname ?? "");
  const isEditorWorkspace = pathname === "/editors" || pathname?.startsWith("/editors/");
  const isDashboard = pathname === "/dashboard" || pathname === "/dashboard/";
  const isFeedbackButtonFull =
    pathname === "/project" ||
    pathname === "/project/" ||
    pathname === "/dashboard";

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [showNudge, setShowNudge] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Dismiss on outside click / Escape. No backdrop → the rest of the UI stays
  // fully interactive while the popover is open.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    const neverShow = window.localStorage.getItem(FEEDBACK_NUDGE_NEVER_SHOW_KEY);
    const shownThisSession = window.sessionStorage.getItem(FEEDBACK_NUDGE_SESSION_KEY);
    if (isOnboarding || isEditorWorkspace || isDashboard || neverShow === "true" || shownThisSession === "true") return;

    const timer = window.setTimeout(() => {
      if (!open) {
        setShowNudge(true);
        trackGaEvent("feedback_nudge_shown");
      }
    }, FEEDBACK_NUDGE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [open, isOnboarding, isEditorWorkspace, isDashboard]);

  useEffect(() => {
    if (isOnboarding) {
      setOpen(false);
      setShowNudge(false);
    }
  }, [isOnboarding]);

  useEffect(() => {
    if (isEditorWorkspace || isDashboard) setShowNudge(false);
  }, [isEditorWorkspace, isDashboard]);

  function openWidget() {
    setShowNudge(false);
    setStatus("idle");
    setError(null);
    setOpen(true);
    trackGaEvent("feedback_open");
  }

  function dismissNudge() {
    setShowNudge(false);
    window.sessionStorage.setItem(FEEDBACK_NUDGE_SESSION_KEY, "true");
    trackGaEvent("feedback_nudge_dismissed");
  }

  function hideNudgeForever() {
    setShowNudge(false);
    window.localStorage.setItem(FEEDBACK_NUDGE_NEVER_SHOW_KEY, "true");
    window.sessionStorage.setItem(FEEDBACK_NUDGE_SESSION_KEY, "true");
    trackGaEvent("feedback_nudge_never_show");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "loading") return;
    if (message.trim().length < 2) {
      setError(t("feedback.error"));
      return;
    }
    setStatus("loading");
    setError(null);
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message.trim(),
          category,
          path: window.location.pathname,
          company,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || t("feedback.genericError"));
      }
      trackGaEvent("feedback_submitted", { category: category || undefined });
      setStatus("done");
      setMessage("");
      setCategory(null);
      setShowNudge(false);
      window.sessionStorage.setItem(FEEDBACK_NUDGE_SESSION_KEY, "true");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t("feedback.genericError"));
    }
  }

  function toggle() {
    setOpen((o) => {
      const next = !o;
      if (next) {
        setStatus("idle");
        setError(null);
        setShowNudge(false);
        trackGaEvent("feedback_open");
      }
      return next;
    });
  }

  // The immersive editor owns the viewport; a floating launcher can cover
  // review/save controls, particularly on mobile. Feedback remains on /editors.
  if (isOnboarding || isEditorProject) return null;

  return (
    <div ref={rootRef} className={cn("fixed start-4 z-40 print:hidden", isEditorProject ? "bottom-20" : "bottom-4")}>
      {showNudge && !isEditorWorkspace && !isDashboard && (
        <div className="mb-3 w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-border bg-background p-4 shadow-2xl">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold">{t("feedback.thanksUsing")}</p>
            <button
              type="button"
              onClick={dismissNudge}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t("feedback.nudgeDismiss")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("feedback.nudgeBody")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openWidget}
              className="rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground"
            >
              {t("feedback.send")}
            </button>
            <button
              type="button"
              onClick={dismissNudge}
              className="rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              {t("feedback.later")}
            </button>
          </div>
          <button
            type="button"
            onClick={hideNudgeForever}
            className="mt-2 text-[11px] text-muted-foreground underline hover:text-foreground"
          >
            {t("feedback.never")}
          </button>
        </div>
      )}

      {open && (
        <div className="mb-3 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
          {status === "done" ? (
            <div className="p-5 text-center">
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Check className="h-5 w-5" />
              </div>
              <p className="font-semibold">{t("feedback.thankYou")}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("feedback.thankYouBody")}
              </p>
              <button
                onClick={() => {
                  setStatus("idle");
                  setOpen(false);
                }}
                className="mt-4 text-sm font-medium text-accent hover:underline"
              >
                {t("feedback.close")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{t("feedback.sendUs")}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("feedback.audience")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Close feedback"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-2 flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setCategory(category === c.id ? null : c.id)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs transition-colors",
                      category === c.id
                        ? "border-accent bg-accent text-accent-foreground"
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {t(c.labelKey)}
                  </button>
                ))}
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                autoFocus
                maxLength={4000}
                placeholder={t("feedback.placeholder")}
                className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-accent"
              />

              {/* Honeypot — hidden from real users. */}
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90 disabled:opacity-60"
              >
                {status === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {t("feedback.send")}
              </button>

              <p className="mt-2 text-center text-[11px] text-muted-foreground">
                {t("feedback.preferEmail")}{" "}
                <a href="mailto:hello@novilot.com" className="text-accent hover:underline">
                  hello@novilot.com
                </a>
              </p>
            </form>
          )}
        </div>
      )}

      <button
        onClick={open ? toggle : openWidget}
        aria-expanded={open}
        aria-label={t("feedback.send")}
        title={t("feedback.send")}
        className={cn(
          // Icon-only circle on phones; full pill with label from `sm` up.
          "flex items-center rounded-full p-3 text-sm font-medium shadow-lg transition-all hover:shadow-xl sm:px-4 sm:py-2.5",
          "gap-2",
          open
            ? "bg-muted text-foreground"
            : "bg-accent text-accent-foreground hover:-translate-y-0.5",
          !isFeedbackButtonFull && "sm:px-3 sm:py-3",
        )}
      >
        <MessageSquarePlus className="h-4 w-4" />
        {isFeedbackButtonFull && <span className="hidden sm:inline">{t("feedback.send")}</span>}
      </button>
    </div>
  );
}
